import { Injectable } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Firestore, doc, updateDoc } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { AuthProvider, AuthUser } from './auth-provider';
import { UserDataProvider, UserData } from './user-data-provider';
import { AlertService } from '../alert/alert.service';
import { Logger } from '../logs/logger.service';
import { RoleService } from './role.service';
import { StorageService } from '../device/storage/storage.service';
import { DataService } from '../device/local/data.service';

/**
 * Thrown by {@link AuthService.login} when credentials are valid but the user's
 * account has been deactivated (`active === false`). Callers catch this to show
 * the dedicated "Account paused" screen instead of an inline wrong-password error.
 */
export const ACCOUNT_DEACTIVATED = 'ACCOUNT_DEACTIVATED';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Public state observables
  private currentUser$ = new BehaviorSubject<AuthUser | null>(null);
  private userData$ = new BehaviorSubject<UserData | null>(null);
  private isLoggedIn$ = new BehaviorSubject<boolean>(false);

  // Public observable properties
  readonly currentUser = this.currentUser$.asObservable();
  readonly userData = this.userData$.asObservable();
  readonly isLoggedIn = this.isLoggedIn$.asObservable();
  readonly authStatus = this.currentUser$.asObservable(); // For backward compatibility

  constructor(
    private authProvider: AuthProvider,
    private userDataProvider: UserDataProvider,
    private roleService: RoleService,
    private alertService: AlertService,
    private logger: Logger,
    private router: Router,
    private storageService: StorageService,
    private auth: Auth,
    private firestore: Firestore,
    private localData: DataService
  ) {
    this.initializeAuthState();
  }

  /**
   * Initialize auth state listener from AuthProvider
   * Called during service construction to set up reactive auth state
   */
  initializeAuthState(): void {
    this.authProvider
      .onAuthStateChanged()
      .pipe(
        switchMap(authUser => {
          if (authUser) {
            this.currentUser$.next(authUser);
            this.isLoggedIn$.next(true);
            return this.userDataProvider.getUserData(authUser.uid).pipe(
              catchError(error => {
                this.logger.warn(`[AuthService] Failed to load user data: ${error?.message || error}`);
                return of(null);
              })
            );
          } else {
            this.currentUser$.next(null);
            this.userData$.next(null);
            this.isLoggedIn$.next(false);
            Sentry.setUser(null);
            return of(null);
          }
        })
      )
      .subscribe(userData => {
        if (userData) {
          const currentUser = this.currentUser$.value;
          this.userData$.next(userData);
          // Tag Sentry errors/replays with who hit them
          Sentry.setUser({ id: currentUser?.uid ?? userData.id, username: userData.display || undefined });
          Sentry.setTag('role', userData.role ?? 'unknown');
          // Set user role in RoleService for directive/component access
          if (userData.role) {
            this.roleService.setUserRole(userData.role?.toLowerCase() as any);
          }
          // Populate localData.userLoggedIn for backward compatibility
          if (currentUser) {
            this.localData.userLoggedIn = {
              id: currentUser.uid,
              displayName: userData.display || '',
              email: userData.email || '',
              badge: this.getRoleBadge(userData.role),
              clientId: ''
            };
          }
        }
      });
  }

  /**
   * Login with email and password
   * Coordinates authentication via AuthProvider and user data loading via UserDataProvider
   * @param email User email
   * @param password User password
   * @returns Promise resolving to AuthUser on success, null on failure
   */
  async login(email: string, password: string): Promise<AuthUser | null> {
    // Note: Callers (app.component.ts, login.page.ts) handle loading indicators
    try {
      // Clear logout flag on explicit login
      await this.storageService.removeKey('userLoggedOut');

      // Sign in via AuthProvider
      const authUser = await this.authProvider.signIn(email, password);

      if (!authUser) {
        this.logger.warn(`Login failed for email: ${email}`);
        return null;
      }

      // Update current user
      this.currentUser$.next(authUser);

      // Load user data
      const userData = await firstValueFrom(this.userDataProvider.getUserData(authUser.uid).pipe(take(1)));
      console.log("[AuthService] userData:", userData);

      // Check if account is active. Surface deactivation as a typed signal so the
      // sign-in UI can show the dedicated "Account paused" screen (no alert dialog).
      if (userData && userData.active === false) {
        this.logger.warn(`[AuthService] User account is inactive: ${authUser.uid}`);

        // Sign out immediately
        await this.authProvider.signOut();

        throw new Error(ACCOUNT_DEACTIVATED);
      }

      this.userData$.next(userData);

      // Set user role in RoleService for directive/component access
      if (userData?.role) {
        this.roleService.setUserRole(userData.role?.toLowerCase() as any);
      }

      // Populate localData.userLoggedIn for backward compatibility
      if (userData) {
        this.localData.userLoggedIn = {
          id: authUser.uid,
          displayName: userData.display || '',
          email: userData.email || '',
          badge: this.getRoleBadge(userData.role),
          clientId: ''
        };
      }

      // Update login state
      this.isLoggedIn$.next(true);

      // Update last login time (fire-and-forget, don't block login)
      this.userDataProvider.setLastLogIn(authUser.uid).catch(err =>
        this.logger.warn(`Failed to update lastLogIn: ${err.message}`)
      );

      this.logger.info(`User logged in: ${email}`);
      return authUser;
    } catch (error: unknown) {
      // Re-throw the deactivated signal so callers can route to the paused screen.
      if (error instanceof Error && error.message === ACCOUNT_DEACTIVATED) {
        throw error;
      }
      this.logger.error(`Login error: ${error instanceof Error ? error.message : String(error)}`);
      return null;
    }
  }

  /**
   * Logout current user
   * Clears auth state, signs out via AuthProvider, and navigates to login
   * @returns Promise that resolves when logout is complete
   */
  async logout(): Promise<void> {
    try {
      // Purge all stored keys (incl. any legacy saved credentials),
      // then mark explicit logout so AutoLoginGuard stays on the login page
      await this.storageService.clearKeys();
      await this.storageService.setKey('userLoggedOut', 'true');

      // Sign out via AuthProvider
      await this.authProvider.signOut();

      // Clear all state
      this.currentUser$.next(null);
      this.userData$.next(null);
      this.isLoggedIn$.next(false);
      this.roleService.clearRole();
      // Clear localData.userLoggedIn for backward compatibility
      this.localData.userLoggedIn = {
        id: '',
        displayName: '',
        email: '',
        badge: '',
        clientId: ''
      };

      // Log logout
      this.logger.info('User logged out successfully');

      // Show user feedback
      await this.alertService.presentAlert('Logged Out', '', 'You have been successfully logged out.');

      // Navigate to login page
      await this.router.navigate(['/login']);
    } catch (error: unknown) {
      this.logger.error(`Logout error: ${error instanceof Error ? error.message : String(error)}`);
      // Even on error, navigate away
      await this.router.navigate(['/login']);
    }
  }

  /**
   * Register new user with email, password and profile data
   * Supports two signatures for backward compatibility:
   * - register(email, password, userData) - new API
   * - register(userData) - legacy API where userData contains email and password
   */
  async register(emailOrUserData: string | any, password?: string, userData?: Partial<UserData>): Promise<AuthUser | null> {
    // Note: Callers handle loading indicators
    try {
      // Normalize parameters based on signature used
      let email: string;
      let pwd: string;
      let profile: Partial<UserData>;

      if (typeof emailOrUserData === 'string') {
        // New signature: register(email, password, userData)
        email = emailOrUserData;
        pwd = password ?? '';
        profile = userData || {};

        // Validate required parameters
        if (!pwd) {
          throw new Error('Password is required');
        }
      } else {
        // Legacy signature: register(userData) where userData contains email and password
        const data = emailOrUserData as any;
        email = data.email ?? '';
        pwd = data.password ?? data.pwd ?? '';
        profile = data;

        // Validate required parameters
        if (!email || !pwd) {
          throw new Error('Email and password are required');
        }
      }

      // Create auth account
      const authUser = await this.authProvider.signUp(email, pwd);

      if (!authUser) {
        this.logger.warn(`Registration failed for email: ${email}`);
        return null;
      }

      // Prepare user profile data (strip sensitive fields)
      const safeProfile = { ...(profile as any) };
      delete safeProfile.password;
      delete safeProfile.pwd;
      const userProfile: Partial<UserData> = {
        id: authUser.uid,
        email: authUser.email || email,
        ...safeProfile
      };

      // Save user profile to Firestore (create new document for new user)
      await this.userDataProvider.createUserData(authUser.uid, userProfile);

      // Update service state
      this.currentUser$.next(authUser);
      // Set user role in RoleService for directive/component access
      if (userProfile.role) {
        this.roleService.setUserRole(userProfile.role?.toLowerCase() as any);
      }
      this.userData$.next(userProfile as UserData);
      this.isLoggedIn$.next(true);

      this.logger.info(`User registered successfully: ${email}`);
      return authUser;
    } catch (error: unknown) {
      this.logger.error(`Registration error: ${error instanceof Error ? error.message : String(error)}`);
      return null;
    }
  }

  /**
   * Get current user Observable (reactive)
   */
  getCurrentUserObservable(): Observable<AuthUser | null> {
    return this.currentUser$.asObservable();
  }

  /**
   * Get current user synchronously
   */
  getCurrentUserSync(): AuthUser | null {
    return this.currentUser$.getValue();
  }

  /**
   * Get current user data Observable
   */
  getCurrentUserData(): Observable<UserData | null> {
    return this.userData$.asObservable();
  }

  /**
   * Get current user data synchronously
   */
  getCurrentUserDataSync(): UserData | null {
    return this.userData$.getValue();
  }

  /**
   * Check if user is logged in (reactive)
   */
  getIsLoggedIn(): Observable<boolean> {
    return this.isLoggedIn$.asObservable();
  }

  /**
   * Check if user is logged in synchronously
   */
  isLoggedInSync(): boolean {
    return this.isLoggedIn$.getValue();
  }

  /**
   * Check if current user has a specific role
   */
  hasRole(role: string): Observable<boolean> {
    return this.userData$.pipe(map(user => user?.role === role || false));
  }

  /**
   * Get current user's role
   */
  getUserRole(): Observable<string | null> {
    return this.userData$.pipe(map(user => user?.role ?? null));
  }

  /**
   * Update current user's profile data
   * @param updates Partial user data to update
   * @returns Promise that resolves when update is complete
   */
  async updateUserProfile(updates: Partial<UserData>): Promise<void> {
    try {
      const currentUser = this.currentUser$.getValue();

      if (!currentUser) {
        this.logger.warn('Cannot update profile: user not authenticated');
        throw new Error('User not logged in');
      }

      // Update in Firestore
      await this.userDataProvider.updateUserData(currentUser.uid, updates);

      // Update local state
      const currentData = this.userData$.getValue();
      if (currentData) {
        this.userData$.next({ ...currentData, ...updates });
      }

      this.logger.info(`Profile updated for user ${currentUser.uid}`);
    } catch (error: unknown) {
      this.logger.error(`Profile update error: ${error instanceof Error ? error.message : String(error)}`);
      throw error;
    }
  }

  /**
   * Request password reset email
   * @param email Email address to send reset link to
   * @returns Promise that resolves when reset email is sent
   */
  async resetPassword(email: string): Promise<void> {
    try {
      await this.authProvider.resetPassword(email);

      this.logger.info(`Password reset email sent to ${email}`);
      await this.alertService.presentAlert('Password Reset', '', 'Check your email for password reset instructions.');
    } catch (error: unknown) {
      this.logger.error(`Password reset error: ${error instanceof Error ? error.message : String(error)}`);
      throw error;
    }
  }

  /**
   * Send a password-reset link without any alert dialog (the styled `/forgot`
   * flow shows its own inline "check your inbox" step).
   * @param email Email address to send the reset link to
   */
  async sendResetLink(email: string): Promise<void> {
    await this.authProvider.resetPassword(email);
    this.logger.info(`Password reset link sent to ${email}`);
  }

  /**
   * Verify a reset oobCode from the email link, returning the owning email.
   */
  verifyResetCode(oobCode: string): Promise<string> {
    return this.authProvider.verifyPasswordResetCode(oobCode);
  }

  /**
   * Complete a password reset with the oobCode + new password.
   */
  completePasswordReset(oobCode: string, newPassword: string): Promise<void> {
    return this.authProvider.confirmPasswordReset(oobCode, newPassword);
  }

  /**
   * Subscribe to auth state changes from provider
   */
  onAuthStateChanged(): Observable<AuthUser | null> {
    return this.authProvider.onAuthStateChanged();
  }

  /**
   * Get auth token for API requests
   */
  getAuthToken(): Observable<string | null> {
    return this.authProvider.getCurrentAuthToken();
  }

  /**
   * Get auth token synchronously
   */
  async getAuthTokenSync(): Promise<string | null> {
    return await firstValueFrom(this.getAuthToken());
  }

  /**
   * Cleanup on logout
   */
  private cleanup(): void {
    this.currentUser$.next(null);
    this.userData$.next(null);
    this.isLoggedIn$.next(false);
    this.roleService.clearRole();
  }

  // Legacy methods for backward compatibility - to be refactored in future tasks

  /**
   * Get current user ID
   * @deprecated Use getCurrentUserSync() instead
   */
  getUserId(): string | null {
    return this.currentUser$.getValue()?.uid ?? null;
  }

  /**
   * Get current user email
   * @deprecated Use getCurrentUserSync() instead
   */
  getUserEmail(): string | null {
    return this.userData$.getValue()?.email ?? null;
  }

  /**
   * Get current user display name
   * @deprecated Use getCurrentUserSync() instead
   */
  async getUserDisplayName(): Promise<string | null> {
    return this.userData$.getValue()?.display ?? null;
  }

  /**
   * Check if user is admin
   * @deprecated Use hasRole('admin') instead
   */
  async isUserAdmin(_userId: string): Promise<boolean> {
    // This will be implemented in Task C5
    return false;
  }

  /**
   * Check if user is client
   * @param userId The user's Firebase UID
   * @returns Promise<true> if user is client, false otherwise
   */
  async isUserClient(userId: string): Promise<boolean> {
    return this.roleService.isUserClient(userId);
  }

  /**
   * Check if user is manager
   * @deprecated Use hasRole('manager') instead
   */
  async isUserManager(_user: any): Promise<boolean> {
    // This will be implemented in Task C5
    return false;
  }

  /**
   * Check if user is tech
   * @deprecated Use hasRole('tech') instead
   */
  async isUserTech(_user: any): Promise<boolean> {
    // This will be implemented in Task C5
    return false;
  }

  /**
   * Check if user is mate
   * @deprecated Use hasRole('mate') instead
   */
  async isUserMate(_user: any): Promise<boolean> {
    // This will be implemented in Task C5
    return false;
  }

  /**
   * Check if user can access application
   * @deprecated Use hasRole() instead
   */
  async canUserAccess(_userId: any): Promise<boolean> {
    // This will be implemented in Task C5
    return false;
  }

  /**
   * Check if user is app client
   * @deprecated Use hasRole('client') instead
   */
  async isAppClient(_userId: string): Promise<boolean> {
    // This will be implemented in Task C5
    return false;
  }

  /**
   * Check if user has multiple roles
   * @deprecated Use hasRole() instead
   */
  hasRoles(roles: string[]): boolean {
    return this.roleService.hasAnyRole(roles as any);
  }

  /**
   * Get badge text from user role
   * @param role The user's role (admin, manager, captain, client, etc.)
   * @returns Badge text for display
   */
  private getRoleBadge(role: string | null | undefined): string {
    if (!role) {return 'User';}
    const roleLower = role.toLowerCase();
    switch (roleLower) {
      case 'admin':
        return 'Admin';
      case 'manager':
        return 'Manager';
      case 'captain':
        return 'Captain';
      case 'client':
        return 'Client';
      case 'tech':
        return 'Tech';
      case 'mate':
        return 'Mate';
      default:
        return 'User';
    }
  }

  /**
   * Re-login with email and password
   * @deprecated Use login() instead
   */
  async reLogin(_email: string, _password: string): Promise<any> {
    // This will be implemented in Task C2
    return null;
  }

  /**
   * Register client with email and displayName
   * @deprecated Use register() instead
   */
  async registerClient(_data: any): Promise<any> {
    // This will be implemented in Task C4
    return null;
  }

  /**
   * Register client from mobile app
   * @deprecated Use register() instead
   */
  async registerClientFromMobileApp(data: any, password: string): Promise<any> {
    try {
      // Create the Firebase Auth account (this also signs the user in)
      const authUser = await this.authProvider.signUp(data.email, password);
      if (!authUser) {
        this.logger.warn(`Registration failed for email: ${data.email}`);
        return null;
      }

      // Create the CLIENT user document (typed as any so clientId passes through to Firestore)
      const userProfile: any = {
        id: authUser.uid,
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        display: data.fullName,
        role: 'CLIENT',
        accountType: 'client',
        appRoles: ['CLIENT'],
        inspector: false,
        clientId: data.id,
        lastLogIn: '',
        chats: [],
        clientChatId: '',
        permissions: {
          pushNotifications: false,
          emailNotifications: false,
        },
      };
      await this.userDataProvider.createUserData(authUser.uid, userProfile);

      // Update the matching client record (inline to avoid a circular dependency on ClientService)
      const clientRef = doc(this.firestore, `clients/${data.id}`);
      await updateDoc(clientRef, { email: data.email, doesClientHaveMobileApp: true });

      // Update in-memory auth state
      this.currentUser$.next(authUser);
      this.roleService.setUserRole('client');
      this.userData$.next(userProfile as UserData);
      this.isLoggedIn$.next(true);

      this.logger.info(`Client registered successfully: ${data.email}`);
      return authUser;
    } catch (error: unknown) {
      this.logger.error(`Registration error: ${error instanceof Error ? error.message : String(error)}`);
      return null;
    }
  }

  /**
   * Check for email in storage
   * @deprecated Use AuthProvider instead
   */
  async checkForEmail(): Promise<string | null> {
    // This will be implemented in Task C2
    return null;
  }

  /**
   * Anonymous login
   * @deprecated Use AuthProvider instead
   */
  anonymousLogin(): boolean {
    // This will be implemented in future tasks
    return false;
  }

  /**
   * Link anonymous account to email account
   * @deprecated Use AuthProvider instead
   */
  async linkAnonymousToNewAccount(_data: any): Promise<void> {
    // This will be implemented in future tasks
  }

  /**
   * Load current user
   * @deprecated Use getCurrentUser() instead
   */
  async loadCurrentUser(): Promise<void> {
    // This will be implemented in Task C2
  }

  /**
   * Get user subject Observable
   * @deprecated Use getCurrentUser() instead
   */
  getUserSubject(): Observable<AuthUser | null> {
    return this.currentUser$.asObservable();
  }

  /**
   * Update display name
   * @deprecated Use updateUserProfile() instead
   */
  async updateDisplayName(_displayName: string): Promise<void> {
    // This will be implemented in Task C5
  }

  /**
   * Update display name for client
   * @deprecated Use updateUserProfile() instead
   */
  async updateDisplayNameClient(_user: any, _displayName: string): Promise<void> {
    // This will be implemented in Task C5
  }

  /**
   * Send password reset
   * @deprecated Use resetPassword() instead
   */
  async sendPasswordReset(): Promise<void> {
    // This will be implemented in Task C5
  }

  /**
   * Send password reset to email
   * @deprecated Use resetPassword() instead
   */
  async sendPwReset(_email: string): Promise<void> {
    // This will be implemented in Task C5
  }

  /**
   * Get current user data
   * @deprecated Use getCurrentUserData() instead
   */
  async getCurrentUser(): Promise<boolean> {
    // This will be implemented in Task C2
    return false;
  }

  /**
   * Delete user account
   * @deprecated Use in future tasks
   */
  async deleteAccount(): Promise<void> {
    // This will be implemented in future tasks
  }
}
